import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, PhoneCall, MessageSquare, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

/**
 * SignalWire Inbound Audit
 * -----------------------------------------------------------------
 * Admin-only. Shows every SignalWire rotation number's inbound Voice
 * and SMS routing state, capability flags, and TCR/10DLC provisioning
 * status. Preview Changes → Apply Selected / Apply All Eligible.
 *
 * Never touches outbound SIP / WebRTC / TURN / caller-ID rotation.
 */

const STATUS_BADGE = {
  correct:              "bg-emerald-100 text-emerald-800 border-emerald-200",
  missing:              "bg-amber-100 text-amber-800 border-amber-200",
  incorrect:            "bg-orange-100 text-orange-800 border-orange-200",
  sms_not_provisioned:  "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABEL = {
  correct:              "Correct",
  missing:              "Missing",
  incorrect:            "Incorrect",
  sms_not_provisioned:  "SMS Not Provisioned",
};

function shortUrl(u) {
  if (!u) return "—";
  return u.replace(/^https?:\/\//, "");
}

export default function SignalWireInboundAudit() {
  const [audit,     setAudit]     = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [selected,  setSelected]  = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/signalwire/inbound-audit");
      setAudit(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const rows     = audit?.numbers || [];
  const summary  = audit?.summary || {};
  const canon    = audit?.canonical || {};

  // "Eligible" = anything the API can actually fix. sms_not_provisioned
  // numbers still surface here for their voice fix or to pre-arm a
  // stale SMS URL off the preview host.
  const eligibleRows = useMemo(
    () => rows.filter((r) => r.voice_status !== "correct"
                          || (r.capabilities?.sms && r.sms_status !== "correct")
                          || r.sms_stale),
    [rows],
  );

  const toggleOne = (sid) => {
    const next = new Set(selected);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === eligibleRows.length) setSelected(new Set());
    else setSelected(new Set(eligibleRows.map((r) => r.sid)));
  };

  const runApply = async (payload, label) => {
    if (!window.confirm(`${label}?\n\nCanonical Voice: ${canon.voice_url}\nCanonical SMS: ${canon.sms_url}\n\nOnly touches inbound routing — outbound SIP / dialer / rotation unaffected.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/signalwire/inbound-apply", payload);
      const s = data.summary || {};
      toast.success(`Voice updated ${s.voice_updated || 0} · SMS updated ${s.sms_updated || 0}${s.sms_skipped_not_provisioned ? ` · Skipped (no campaign) ${s.sms_skipped_not_provisioned}` : ""}${s.voice_failed || s.sms_failed ? ` · Failed ${(s.voice_failed || 0) + (s.sms_failed || 0)}` : ""}`, { duration: 6000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  const applySelected = () => {
    if (selected.size === 0) { toast.error("Select at least one number"); return; }
    runApply({ sids: Array.from(selected), fix_voice: true, fix_sms: true },
             `Apply to ${selected.size} selected number${selected.size > 1 ? "s" : ""}`);
  };
  const applyAll = () => {
    runApply({ apply_all_eligible: true, fix_voice: true, fix_sms: true },
             `Apply to all ${eligibleRows.length} eligible numbers`);
  };

  if (loading) {
    return (
      <div className="mt-3 rounded-xl border border-purple-200 bg-white p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" /> Loading SignalWire audit…
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between">
        <span>Couldn&apos;t reach SignalWire audit.</span>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full text-xs">
          <RefreshCw size={12} className="mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-purple-200 bg-white p-4" data-testid="sw-inbound-audit">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">
            SignalWire Inbound Audit
            <span className="text-[10px] font-normal text-muted-foreground px-1.5 py-0.5 rounded bg-slate-100">
              Admin only
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed max-w-[520px]">
            Every rotation number&apos;s inbound Voice + SMS resource. Fixes point at production
            <code className="mx-1 px-1 rounded bg-slate-100 text-[10px]">{shortUrl(canon.voice_url)}</code>
            and
            <code className="mx-1 px-1 rounded bg-slate-100 text-[10px]">{shortUrl(canon.sms_url)}</code>
            (AI SMS Qualifier). Never touches outbound SIP, WebRTC, TURN/ICE, or the caller-ID rotation.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load}
                className="rounded-full text-xs" data-testid="sw-audit-refresh-btn">
          <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">Total <b>{summary.total}</b></span>
        <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">Correct <b>{summary.correct}</b></span>
        <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800">Missing <b>{summary.missing}</b></span>
        <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800">Incorrect <b>{summary.incorrect}</b></span>
        <span className="px-2 py-1 rounded-full bg-red-100 text-red-800">SMS Not Provisioned <b>{summary.sms_not_provisioned}</b></span>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={toggleAll}
                disabled={eligibleRows.length === 0}
                className="rounded-full text-xs" data-testid="sw-audit-select-all-btn">
          {selected.size === eligibleRows.length && eligibleRows.length > 0 ? "Deselect All Eligible" : `Select All Eligible (${eligibleRows.length})`}
        </Button>
        <Button size="sm" onClick={applySelected} disabled={busy || selected.size === 0}
                className="rounded-full text-xs bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="sw-audit-apply-selected-btn">
          {busy ? (<><Loader2 size={12} className="mr-1 animate-spin" /> Applying…</>)
                : `Apply Selected (${selected.size})`}
        </Button>
        <Button size="sm" onClick={applyAll} disabled={busy || eligibleRows.length === 0}
                className="rounded-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="sw-audit-apply-all-btn">
          {busy ? (<><Loader2 size={12} className="mr-1 animate-spin" /> Applying…</>)
                : `Apply All Eligible (${eligibleRows.length})`}
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="px-2 py-2 w-8"></th>
              <th className="px-2 py-2 text-left">Number</th>
              <th className="px-2 py-2 text-left">Caps</th>
              <th className="px-2 py-2 text-left">Voice Resource</th>
              <th className="px-2 py-2 text-left">SMS Resource</th>
              <th className="px-2 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const cap = r.capabilities || {};
              const isEligible = r.voice_status !== "correct"
                              || (cap.sms && r.sms_status !== "correct");
              return (
                <tr key={r.sid}
                    data-testid={`sw-audit-row-${r.phone_number}`}
                    className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.sid)}
                      disabled={!isEligible}
                      onChange={() => toggleOne(r.sid)}
                      data-testid={`sw-audit-select-${r.phone_number}`}
                    />
                  </td>
                  <td className="px-2 py-2 font-mono font-semibold text-slate-800">
                    {r.phone_number}
                    {r.outbound_active === false && (
                      <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title="Parked — not in outbound rotation">
                        parked
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[10px] text-slate-600">
                    <span className="mr-1.5" title="Voice">📞 {cap.voice ? "Y" : "—"}</span>
                    <span className={cap.sms ? "text-emerald-700 font-semibold" : "text-red-600"}
                          title="SMS (reflects 10DLC campaign provisioning)">
                      💬 {cap.sms ? "Y" : "N"}
                    </span>
                    <span className="ml-1.5 text-slate-500" title="MMS">
                      🖼️ {cap.mms ? "Y" : "—"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-[10px]">
                    <div className="flex items-center gap-1">
                      {r.voice_status === "correct" ? <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                        : r.voice_status === "missing" ? <AlertTriangle size={11} className="text-amber-600 shrink-0" />
                        : <XCircle size={11} className="text-orange-600 shrink-0" />}
                      <span className="truncate max-w-[220px] font-mono" title={r.voice_url || "not set"}>
                        {shortUrl(r.voice_url)}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-[10px]">
                    <div className="flex items-center gap-1">
                      {r.sms_status === "correct" ? <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                        : r.sms_status === "sms_not_provisioned" ? <XCircle size={11} className="text-red-500 shrink-0" />
                        : r.sms_status === "missing" ? <AlertTriangle size={11} className="text-amber-600 shrink-0" />
                        : <XCircle size={11} className="text-orange-600 shrink-0" />}
                      <span className="truncate max-w-[220px] font-mono" title={r.sms_url || "not set"}>
                        {r.sms_status === "sms_not_provisioned"
                          ? (r.sms_stale ? "STALE preview URL — pre-arm to canonical" : "requires 10DLC campaign")
                          : shortUrl(r.sms_url)}
                      </span>
                    </div>
                    {r.blocker && (
                      <div className="text-[9px] text-red-600 mt-0.5 font-mono">
                        {r.blocker === "10dlc_campaign_required" ? "10DLC campaign required"
                          : r.blocker === "webhook_missing" ? "webhook missing"
                          : "webhook wrong URL"}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${STATUS_BADGE[r.status] || ""}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-4 text-muted-foreground text-xs">No rotation numbers found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Campaign guidance */}
      {(summary.sms_not_provisioned || 0) > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-[11px] text-red-800 flex gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>
            <b>{summary.sms_not_provisioned}</b> number{summary.sms_not_provisioned > 1 ? "s are" : " is"} not SMS-provisioned.
            Under US carrier rules, 10DLC messaging traffic requires each number to be attached to an approved Campaign Registry campaign
            before texts (inbound OR outbound) will deliver.
            <div className="mt-1.5 text-red-700">
              Fix: SignalWire portal → <b>Messaging → Campaign Registry</b> → attach these numbers to your approved 10DLC campaign,
              then click <b>Refresh</b> above. Once <code>sms: true</code> appears in the Caps column, hit <b>Apply Selected</b>
              to wire the inbound SMS webhook.
            </div>
          </div>
        </div>
      )}

      {/* Route legend */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px] text-slate-600">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2 flex items-start gap-1.5">
          <PhoneCall size={12} className="mt-0.5 shrink-0 text-slate-500" />
          <div>
            <b>Voice inbound flow:</b> SignalWire number → <code>{shortUrl(canon.voice_url)}</code> → Retell AI (SIP handoff) → qualification → transcript/summary/score saved to lead.
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2 flex items-start gap-1.5">
          <MessageSquare size={12} className="mt-0.5 shrink-0 text-slate-500" />
          <div>
            <b>SMS inbound flow:</b> SignalWire number → <code>{shortUrl(canon.sms_url)}</code> → AI SMS Qualifier (Claude Sonnet 5) → qualification questions with context → replies from the SAME number → 3/7/14-day follow-ups if seller goes silent.
          </div>
        </div>
      </div>
    </div>
  );
}
